import { CONNECTION_LABELS, socialPrivacy } from '../../../packages/domain/src/02-social.mjs';
import { createDirectChoices } from './15-direct-choices.mjs';

const audienceIcons = {
  public: 'globe',
  'friends-of-friends': 'share-2',
  friends: 'users',
  private: 'lock',
};
/** Present server-owned connection settings and transient guide/dialog drafts.
 * Accepted snapshots remain the only relationship and audience authority. */
export function createSocialControls(ctx) {
  const t = (key, values) => ctx.translate(`social.${key}`, values);
  let profileGeneration = 0;
  function loadProfile(id) {
    const captured = ctx.state,
      actor = captured.me.id,
      route = ctx.route().join('/'),
      generation = ++profileGeneration;
    const status = ctx.e('p', 'subtle', ctx.uiText('Loading…'));
    status.setAttribute('role', 'status');
    ctx.view.append(status);
    ctx
      .api(`/api/profile?id=${encodeURIComponent(id)}`)
      .then((profile) => {
        if (
          generation !== profileGeneration ||
          ctx.state !== captured ||
          ctx.state?.me?.id !== actor ||
          ctx.route().join('/') !== route ||
          !status.isConnected
        )
          return;
        if (!profile?.id) throw new Error(t('empty'));
        if (profile.id !== id)
          ctx.state.routeAliases = { ...ctx.state.routeAliases, [id]: profile.id };
        ctx.state.users = [...ctx.state.users.filter((user) => user.id !== profile.id), profile];
        ctx.render();
      })
      .catch((failure) => {
        if (
          generation !== profileGeneration ||
          ctx.state !== captured ||
          ctx.state?.me?.id !== actor ||
          ctx.route().join('/') !== route ||
          !status.isConnected
        )
          return;
        status.textContent = failure.message;
        ctx.view.append(ctx.button(t('retry'), ctx.render));
      });
  }
  function editConnection(id) {
    const form = ctx.e('form', 'form-stack');
    const choices = createDirectChoices(ctx, {
      label: t('label'),
      variant: 'filter',
      name: 'connection-label',
      value: ctx.state.settings.connectionLabels?.[id] ?? 'friend',
      options: CONNECTION_LABELS.map((value) => ({
        value,
        label: t(`label.${value}`),
        literal: true,
        icon: value === 'friend' ? 'users' : 'heart',
      })),
    });
    const error = ctx.e('p', 'form-error');
    error.setAttribute('role', 'alert');
    const save = ctx.e('button', 'primary-button', t('save'));
    form.append(
      ctx.e('p', 'subtle', t('labelNote')),
      choices.node,
      error,
      ctx.append(
        ctx.e('div', 'dialog-actions'),
        ctx.button(t('cancel'), () => ctx.modal.close()),
        save,
      ),
    );
    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      save.disabled = true;
      choices.node.inert = true;
      try {
        await ctx.command({ type: 'connection-label', id, label: choices.value }, { paint: false });
        if (!form.isConnected || !ctx.modal.open) return;
        ctx.modal.close();
        ctx.render();
      } catch (failure) {
        error.textContent = failure.message;
      } finally {
        save.disabled = false;
        choices.node.inert = false;
      }
    });
    ctx.dialog(t('label'), form);
  }
  function privacyCard() {
    const card = ctx.e('section', 'settings-card social-privacy');
    const form = ctx.e('form', 'form-stack');
    const current = socialPrivacy(ctx.state.settings);
    const choices = Object.keys(current).map((key) => [
      key,
      createDirectChoices(ctx, {
        label: t(`privacy.${key}`),
        variant: 'filter',
        name: `social-${key}`,
        value: current[key],
        options: Object.keys(audienceIcons).map((value) => ({
          value,
          label: t(key === 'requests' && value === 'private' ? 'nobody' : `audience.${value}`),
          literal: true,
          icon: audienceIcons[value],
        })),
      }),
    ]);
    const save = ctx.e('button', 'primary-button', t('save'));
    const error = ctx.e('p', 'form-error');
    error.setAttribute('role', 'alert');
    card.append(ctx.append(ctx.e('h2'), ctx.icon('shield'), document.createTextNode(t('privacy'))));
    form.append(
      ctx.e('p', 'subtle', t('privacyNote')),
      ...choices.map(([, control]) => control.node),
      error,
      save,
    );
    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      save.disabled = true;
      for (const [, control] of choices) control.node.inert = true;
      try {
        await ctx.command(
          {
            type: 'social-privacy',
            privacy: Object.fromEntries(choices.map(([key, control]) => [key, control.value])),
          },
          { paint: false },
        );
        ctx.toast(t('saved'));
      } catch (failure) {
        error.textContent = failure.message;
      } finally {
        save.disabled = false;
        for (const [, control] of choices) control.node.inert = false;
      }
    });
    card.append(
      form,
      ctx.button(t('guide'), () => openGuide()),
    );
    return card;
  }
  function openConnections(id) {
    const actor = ctx.state.me.id;
    const body = ctx.e('div', 'form-stack social-connections');
    const form = ctx.e('form', 'people-search');
    const query = ctx.field('input', {
      type: 'search',
      maxLength: 120,
      placeholder: t('search'),
      ariaLabel: t('search'),
    });
    const submit = ctx.e('button', 'secondary-button', t('search'));
    form.append(query, submit);
    const rows = ctx.e('div', 'request-list');
    rows.setAttribute('aria-live', 'polite');
    let next = null,
      generation = 0;
    const more = ctx.button(t('more'), () => load(next), 'secondary-button');
    more.hidden = true;
    body.append(ctx.e('p', 'subtle', t('browseNote')), form, rows, more);
    ctx.dialog(t('connections'), body);
    async function load(offset = null) {
      const request = ++generation;
      const reset = offset === null;
      more.disabled = true;
      if (reset) rows.replaceChildren(ctx.e('p', 'subtle', t('loading')));
      try {
        const result = await ctx.api(
          `/api/connections?id=${encodeURIComponent(id)}&query=${encodeURIComponent(query.value.trim())}${offset === null ? '' : `&offset=${offset}`}`,
        );
        if (
          request !== generation ||
          ctx.state?.me.id !== actor ||
          !body.isConnected ||
          !ctx.modal.open
        )
          return;
        if (reset) rows.replaceChildren();
        for (const user of result.users) {
          const row = ctx.e('div', 'request-card');
          const link = ctx.link(user.name, `#/profile/${user.id}`);
          link.addEventListener('click', () => ctx.modal.close());
          row.append(ctx.avatar(user), link);
          rows.append(row);
        }
        if (!rows.children.length) rows.append(ctx.e('p', 'subtle', t('empty')));
        next = result.next;
        more.hidden = next === null;
      } catch (failure) {
        if (request !== generation || ctx.state?.me.id !== actor || !body.isConnected) return;
        rows.replaceChildren(
          ctx.e('p', 'form-error', failure.message),
          ctx.button(t('retry'), () => load(null)),
        );
        more.hidden = true;
      } finally {
        if (request === generation) more.disabled = false;
      }
    }
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      load(null);
    });
    ctx.modal.addEventListener(
      'close',
      () => {
        generation += 1;
      },
      { once: true },
    );
    load();
  }
  function openGuide() {
    const body = ctx.e('div', 'form-stack social-guide');
    const stage = ctx.e('div', 'social-guide-stage');
    let index = 0;
    const steps = [
      ['user-plus', 'connect'],
      ['heart', 'organize'],
      ['shield', 'audiences'],
    ];
    const error = ctx.e('p', 'form-error');
    error.setAttribute('role', 'alert');
    const back = ctx.button(t('back'), () => {
      index -= 1;
      draw();
    });
    const next = ctx.button(
      t('next'),
      async () => {
        if (index < steps.length - 1) {
          index += 1;
          draw();
          return;
        }
        await finish('complete');
      },
      'primary-button',
    );
    const skip = ctx.button(t('skip'), () => finish('skip'), 'text-button');
    async function finish(action) {
      for (const control of [back, next, skip]) control.disabled = true;
      try {
        await ctx.command({ type: 'social-guide', action }, { paint: false });
        if (!body.isConnected || !ctx.modal.open) return;
        ctx.modal.close();
        ctx.render();
      } catch (failure) {
        error.textContent = failure.message;
        for (const control of [back, next, skip]) control.disabled = false;
        back.disabled = index === 0;
      }
    }
    function draw() {
      stage.replaceChildren(
        ...steps.map(([icon, key], step) => {
          const panel = ctx.e('section', 'social-guide-step');
          panel.style.visibility = step === index ? 'visible' : 'hidden';
          panel.setAttribute('aria-hidden', String(step !== index));
          panel.append(
            ctx.icon(icon),
            ctx.e('p', 'subtle', `${step + 1} / ${steps.length}`),
            ctx.e('h3', '', t(`guide.${key}`)),
            ctx.e('p', '', t(`guide.${key}.note`)),
          );
          return panel;
        }),
      );
      back.disabled = index === 0;
      next.textContent = t(index === steps.length - 1 ? 'done' : 'next');
    }
    const art = ctx.field('img', {
      src: '/assets/social-connections-guide-v1-640.webp',
      alt: '',
      width: 640,
      height: 427,
    });
    body.append(art, stage, error, ctx.append(ctx.e('div', 'dialog-actions'), skip, back, next));
    ctx.dialog(t('guide'), body);
    draw();
  }
  function guideEntry() {
    const row = ctx.e('section', 'social-guide-entry');
    row.append(ctx.icon('users'), ctx.e('span', '', t('intro')), ctx.button(t('guide'), openGuide));
    return row;
  }
  return { editConnection, privacyCard, openConnections, openGuide, guideEntry, loadProfile };
}
